/**
 * ContactPanel.jsx
 * Who you are talking to, and what your team has decided about them.
 *
 * This is the closest thing the app has to a CRM record, and it is careful
 * about the difference between three kinds of fact:
 *
 *   measured  — how many times they have been in touch, and when. Counted from
 *               rows that exist.
 *   judged    — sentiment, intent, a lead score. Written by a model, marked
 *               with a sparkle, and stamped with when it was judged.
 *   decided   — lead, customer, tags. Chosen by a person, and nothing else may
 *               write them.
 *
 * "Convert to lead" sets a status and records when. It does not push anywhere,
 * because there is nothing behind this app to push to, and the panel says so.
 *
 * Dependencies: services/api.js, shared.js
 */

import { useState } from 'react';
import { Check, Eye, Plus, Sparkle, Users, Warning } from '../Icons.jsx';
import { api } from '../../services/api.js';
import { PLATFORMS } from '../dashboard/helpers.js';
import {
  CONTACT_STATUS,
  INTENT,
  PRIORITY,
  SENTIMENT,
  initials,
  scoreBand,
  snip,
  when,
} from './shared.js';

export default function ContactPanel({ detail, team = [], onChanged, onError, onOpen }) {
  const { item, state, contact, history = [] } = detail;
  const [tag, setTag] = useState('');
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);

  const patch = async (body) => {
    setBusy(true);
    try {
      await api.social.setState(item.kind, item.refId, body);
      await onChanged();
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  };

  const patchContact = async (body) => {
    if (!contact) return;
    setBusy(true);
    try {
      await api.social.updateContact(contact.id, body);
      await onChanged();
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  };

  const status = CONTACT_STATUS[contact?.status ?? 'unknown'];
  const band = scoreBand(state?.lead_score);
  const intent = state?.intent ? INTENT[state.intent] : null;

  return (
    <section className="card contact">
      <div className="card__head">
        <span className="tile">
          <Users size={21} />
        </span>
        <div className="card__head-text">
          <h2>Contact</h2>
          <p>What we know, and what you have decided.</p>
        </div>
      </div>

      <div className="contact__id">
        <span className="conv__avatar conv__avatar--lg">
          <span className="conv__initials">{initials(contact?.name || item.who)}</span>
        </span>
        <div>
          <div className="row__label">{contact?.name || item.who || 'Unknown'}</div>
          <div className="row__sub">
            {PLATFORMS[item.platform]?.label ?? item.platform}
            {contact?.handle ? ` · @${contact.handle}` : ''}
          </div>
        </div>
        <span className={`badge badge--${status.tone === 'muted' ? 'warn' : status.tone}`}>
          {status.label}
        </span>
      </div>

      {/* Measured. Counted from what we have, never from a platform figure we
          have not fetched. */}
      <div className="contact__facts">
        <div>
          <span className="contact__num">{history.length + 1}</span>
          <span className="contact__cap">Interactions</span>
        </div>
        <div>
          <span className="contact__num">{when(contact?.first_seen_at) || '—'}</span>
          <span className="contact__cap">First seen</span>
        </div>
        <div>
          <span className="contact__num">{when(contact?.last_seen_at) || '—'}</span>
          <span className="contact__cap">Last seen</span>
        </div>
      </div>

      {/* Judged. Every row here says a model wrote it, and when. */}
      <div className="contact__block">
        <div className="contact__block-head">
          <Sparkle size={14} /> AI judgement
        </div>

        {state?.triaged_at ? (
          <>
            <div className="contact__row">
              <span>Sentiment</span>
              <span className={`tag tag--${SENTIMENT[state.sentiment]?.tone ?? 'muted'}`}>
                {SENTIMENT[state.sentiment]?.label ?? 'Not judged'}
              </span>
            </div>
            <div className="contact__row">
              <span>What they want</span>
              <span className={`tag tag--${intent?.tone ?? 'muted'}`}>
                {intent?.label ?? 'Not judged'}
              </span>
            </div>
            <div className="contact__row">
              <span>Lead score</span>
              <span className={`tag tag--${band?.tone ?? 'muted'}`}>
                {state.lead_score === null ? '—' : `${state.lead_score}/100`}
                {band ? ` · ${band.label}` : ''}
              </span>
            </div>
            {!!state.spam && (
              <div className="notice notice--error">
                <Warning />
                <span>Judged as spam. Check before replying.</span>
              </div>
            )}
            <p className="contact__stamp">
              Judged {when(state.triaged_at)} by {state.triage_model ?? 'the model'}. An opinion,
              not a measurement.
            </p>
          </>
        ) : (
          <p className="contact__stamp">
            Not judged yet. Run triage from the toolbar to have sentiment, intent and a lead score
            written for the conversations on screen.
          </p>
        )}
      </div>

      {/* Decided. Nothing but a person writes any of this. */}
      <div className="contact__block">
        <div className="contact__block-head">
          <Check size={14} /> Your decisions
        </div>

        <div className="contact__row">
          <span>Assigned to</span>
          <select
            className="select select--sm"
            value={state?.assignee ?? ''}
            disabled={busy}
            onChange={(e) => patch({ assignee: e.target.value || null })}
            aria-label="Assign to"
          >
            <option value="">Nobody</option>
            {team.map((t) => (
              <option key={t.email} value={t.email}>
                {t.name || t.email}
              </option>
            ))}
          </select>
        </div>

        <div className="contact__row">
          <span>Priority</span>
          <select
            className="select select--sm"
            value={state?.priority ?? 'normal'}
            disabled={busy}
            onChange={(e) => patch({ priority: e.target.value })}
            aria-label="Priority"
          >
            {Object.entries(PRIORITY).map(([key, p]) => (
              <option key={key} value={key}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        <div className="contact__row">
          <span>Status</span>
          <select
            className="select select--sm"
            value={contact?.status ?? 'unknown'}
            disabled={busy || !contact}
            onChange={(e) => patchContact({ status: e.target.value })}
            aria-label="Contact status"
          >
            {Object.entries(CONTACT_STATUS).map(([key, s]) => (
              <option key={key} value={key}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <div className="contact__tags">
          {(state?.tags_json ? JSON.parse(state.tags_json) : []).map((t) => (
            <button
              key={t}
              type="button"
              className="tag tag--removable"
              title="Remove"
              onClick={() =>
                patch({ tags: JSON.parse(state.tags_json).filter((x) => x !== t) })
              }
            >
              {t} ×
            </button>
          ))}

          {adding ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const next = tag.trim();
                if (!next) return;
                const current = state?.tags_json ? JSON.parse(state.tags_json) : [];
                patch({ tags: [...new Set([...current, next])] });
                setTag('');
                setAdding(false);
              }}
            >
              <input
                className="tag-input"
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                placeholder="Tag"
                maxLength={30}
                autoFocus
                onBlur={() => setAdding(false)}
              />
            </form>
          ) : (
            <button type="button" className="tag tag--add" onClick={() => setAdding(true)}>
              <Plus size={11} /> Tag
            </button>
          )}
        </div>
      </div>

      <div className="contact__actions">
        {contact?.status !== 'lead' ? (
          <button
            className="btn btn--primary btn--sm"
            disabled={busy || !contact}
            onClick={() => patchContact({ status: 'lead' })}
          >
            <Users size={15} /> Convert to lead
          </button>
        ) : (
          <span className="row__sub">
            Marked as a lead {when(contact.converted_at)}. This is a label in KreeBoom — it has
            not been sent to any CRM.
          </span>
        )}

        <button
          className="btn btn--sm"
          disabled={busy}
          onClick={() => patch({ status: state?.status === 'resolved' ? 'open' : 'resolved' })}
        >
          <Check size={15} /> {state?.status === 'resolved' ? 'Reopen' : 'Mark resolved'}
        </button>
      </div>

      {history.length > 0 && (
        <div className="contact__block">
          <div className="contact__block-head">
            <Eye size={14} /> Earlier from this person
          </div>
          <ul className="contact__history">
            {history.slice(0, 6).map((h) => (
              <li key={`${h.kind}:${h.ref_id}`}>
                <button type="button" onClick={() => onOpen({ kind: h.kind, ref_id: h.ref_id })}>
                  <span>{snip(h.body, 52) || <em>No text</em>}</span>
                  <small>
                    {h.kind === 'comment' ? 'Comment' : 'Message'} · {when(h.at)}
                  </small>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* The honest limit of this record, said where it matters. */}
      <p className="card__footnote">
        Identified per platform. Meta gives no shared id across Facebook and Instagram, so the
        same person on both is two records here.
      </p>
    </section>
  );
}
